// Minimized AI sessions always reopen on the originating entity's picture
// tab, so the upload id derived from the route matches the picture component.
export const buildPictureTabRoute = (path: string) => ({
    path,
    query: { tab: 'picture' }
});

export const AI_IMAGE_EVENTS = {
    URL_CHANGE: "URL_CHANGE",
    CANCEL: 'cancel',
    SAVE: 'save',
    REPLACE: 'replace',
    MINIMIZE: 'minimize',
    EXPAND: 'expand',
    SAVE_BULK: 'save-bulk',
    SAVE_ALL: 'save-all',
    SAVE_PARTIAL: 'save-partial',
};

// Payload contract of the WebAI editor's save messages — mirrors
// appointment-ai/lib/postmessage/types.ts. When imageType is
// 'object' the editor already uploaded the picture to the Omnicasa cloud
// (/files/upload-picture?type=ObjectPicture&tab=6) and `image` is the
// returned ObjectPicture JSON; 'base64' is the legacy data-URI fallback.
export const AI_IMAGE_TYPE = {
    OBJECT: 'object',
    BASE64: 'base64',
};

export type AIPictureObject = Record<string, any>;

export interface IAISavedImageItem {
    image: string | AIPictureObject;
    name: string;
    imageType?: string;
    hasWatermark?: boolean;
}

// `save` — single image; jobId prunes that job from the tracker.
export interface IAISaveMessage extends IAISavedImageItem {
    type: string;
    sessionId?: string | null;
    jobId?: string | null;
    propertyRef?: string | null;
    propertyId?: number | null;
    projectRef?: string | null;
    projectId?: number | null;
}

// `save-all` (batch complete) / `save-partial` (subset saved). The WebApp
// treats both exactly like the single `save`: import the images, drop the
// batch's whole tracker group, close the editor. A bulk message is
// all-object or all-base64, never mixed — the editor aborts the whole
// batch on any upload failure.
export interface IAISaveImagesMessage {
    type: string;
    sessionId: string | null;
    savedJobIds: string[];
    images: IAISavedImageItem[];
    propertyRef?: string | null;
    propertyId?: number | null;
    projectRef?: string | null;
    projectId?: number | null;
}

// The single `save` is a flat one-image message with a singular jobId;
// bulk messages already wrap images[] + savedJobIds[]. Normalize every
// save to the bulk shape so receivers handle one contract.
export function normalizeAISaveMessage(message: IAISaveMessage | IAISaveImagesMessage): IAISaveImagesMessage {
    if ('images' in message && message.images) {
        return message;
    }
    const single = message as IAISaveMessage;
    return {
        type: single.type,
        sessionId: single.sessionId ?? null,
        savedJobIds: single.jobId ? [single.jobId] : [],
        images: [{
            image: single.image,
            name: single.name,
            imageType: single.imageType,
            hasWatermark: single.hasWatermark
        }],
        propertyRef: single.propertyRef,
        propertyId: single.propertyId,
        projectRef: single.projectRef,
        projectId: single.projectId
    };
}
